// MoveItemDialog — pick a new parent folder for an explorer folder or leaf
// row. The leaf segment is unchanged; only the parent prefix moves.

import { useEffect, useMemo, useState } from 'react'

import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Label } from '../ui/label'
import FolderTreePicker from './FolderTreePicker'
import type { ExplorerItemKind } from './item-kind'
import { joinUnderParent, leafSegment, parentPathOf, type PathItem } from './tree'
import { readMutationError } from './mutation-error'

export type MoveItemKind = ExplorerItemKind

export interface MoveItemDialogProps {
  open: boolean
  kind: MoveItemKind
  name: string
  currentPath: string
  folders: readonly PathItem[]
  dashboards: readonly PathItem[]
  disabledPaths?: ReadonlySet<string>
  onClose: () => void
  onSubmit: (destinationFolderPath: string) => Promise<void>
}

const MoveItemDialog = ({
  open,
  kind,
  name,
  currentPath,
  folders,
  dashboards,
  disabledPaths,
  onClose,
  onSubmit
}: MoveItemDialogProps) => {
  const currentParent = parentPathOf(currentPath)
  const [parentPath, setParentPath] = useState(currentParent)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setParentPath(currentParent)
      setError(null)
      setSubmitting(false)
    }
  }, [open, currentParent, currentPath])

  const previewPath = useMemo(
    () => joinUnderParent(parentPath, leafSegment(currentPath)),
    [parentPath, currentPath]
  )
  const unchanged = parentPath === currentParent

  const handleSubmit = async () => {
    if (unchanged) {
      onClose()
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(parentPath)
      onClose()
    } catch (err) {
      setError(readMutationError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <DialogContent className="sm:max-w-120" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Move {kind}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-ink-2">
            Move <strong>{name}</strong> to another folder. The name stays the same.
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="explorer-move-parent">Destination folder</Label>
            <FolderTreePicker
              id="explorer-move-parent"
              folders={folders}
              dashboards={dashboards}
              value={parentPath}
              onChange={setParentPath}
              disabled={submitting}
              disabledPaths={disabledPaths}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-11 uppercase tracking-wider text-ink-3">New path</Label>
            <code className="rounded bg-bg-sunk px-2 py-1 font-mono text-12 text-ink-2">
              /{previewPath}
            </code>
          </div>
          {error ? (
            <div className="rounded-md bg-st-down-bg p-2.5 text-12 text-st-down" role="alert">
              {error}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Moving…' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default MoveItemDialog
